import React, { useMemo } from 'react';
import { Alert, Box, Stack, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';

import TalkingAI from 'app/components/Chat/TalkingAI/TalkingAI.tsx';
import MicrophonePermissionIndicator from '@/features/audio/components/MicrophonePermissionIndicator';
import type { PersonaChatState } from '@/features/personaChat/state/signals.ts';
import { personaChatLastAiText, personaChatLastUserText, personaChatLastAiAt, personaChatLastUserAt } from '@/features/personaChat/state/signals.ts';
import { createTranslationField } from 'app/utils/i18nUtils.ts';
import type { I18nPersonasNs } from '@/i18n/dictionaries/interfaces.ts';
import { I18nNamespace } from '@/i18n/types/i18nNamespace.ts';
import { useSelector } from 'react-redux';
import { CurrentUserSelector } from 'app/selectors/userSelectors';

interface PersonaChatPanelProps {
  isListening: boolean;
  isSpeaking: boolean;
  isRecording: boolean;
  isLoading: boolean;
  isResponding: boolean;
  status: PersonaChatState;
  error?: string | null;
  portraitSrc?: string | null;
  personaName?: string | null;
}


const PersonaChatPanel: React.FC<PersonaChatPanelProps> = ({
  isListening,
  isSpeaking,
  isRecording,
  isLoading,
  isResponding,
  status,
  error,
  portraitSrc,
  personaName,
}) => {
  const { t: translate } = useTranslation(I18nNamespace.Personas);
  const i18n = createTranslationField<I18nPersonasNs>();
  const currentUser = useSelector(CurrentUserSelector);
  const theme = useTheme();

  const statusLabel = useMemo(() => {
    if (isLoading) {
      return translate(i18n.chat.status.startingChat);
    }
    if (status === 'speaking') {
      return translate(i18n.chat.status.personaSpeaking);
    }
    if (status === 'listening') {
      return translate(i18n.chat.status.listening);
    }
    if (status === 'transcribing') {
      return translate(i18n.chat.status.processingInput);
    }
    if (isResponding) {
      return translate(i18n.chat.status.synthesizingResponse);
    }
    if (isRecording) {
      return translate(i18n.chat.status.readyToListen);
    }
    return translate(i18n.chat.status.chatPaused);
  }, [isLoading, status, isResponding, isRecording, translate, i18n]);

  const lastUserText = personaChatLastUserText.value;
  const lastAiText = personaChatLastAiText.value;
  const lastUserAt = personaChatLastUserAt.value ?? -Infinity;
  const lastAiAt = personaChatLastAiAt.value ?? -Infinity;
  const userName = currentUser?.name || currentUser?.email || 'You';
  const aiName = personaName || 'AI Persona';

  const messages = [] as Array<{ type: 'user' | 'ai'; text: string; at: number }>;
  if (lastUserText) messages.push({ type: 'user', text: lastUserText, at: lastUserAt });
  if (lastAiText) messages.push({ type: 'ai', text: lastAiText, at: lastAiAt });
  messages.sort((a, b) => a.at - b.at);

  return (
    <Stack spacing={2} alignItems="stretch" justifyContent="center" sx={{ width: '100%', height: '100%' }}>
      <Box sx={{ alignSelf: 'center' }}>
        <TalkingAI
          isListening={isListening}
          isSpeaking={isSpeaking}
          width={316}
          height={225}
          portraitSrc={portraitSrc ?? undefined}
        />
      </Box>

      {messages.length > 0 && (
        <Box sx={{ width: '100%', px: 2, mt: 1 }}>
          <Stack spacing={1}>
            {messages.map((message, index) => {
              const isLast = index === messages.length - 1;
              const alignRight = message.type === 'user';
              const name = message.type === 'user' ? userName : aiName;
              return (
                <Box key={`${message.type}-${index}`} sx={{ display: 'flex', justifyContent: alignRight ? 'flex-end' : 'flex-start', mt: isLast && messages.length > 1 ? 0.75 : 0 }}>
                  <Box sx={{ maxWidth: '100%', display: 'flex', flexDirection: 'column', alignItems: alignRight ? 'flex-end' : 'flex-start' }}>
                    <Box sx={{ fontWeight: 600 }}>
                      <Typography variant="caption">{name}</Typography>
                    </Box>
                    <Box sx={{
                      display: 'inline-block',
                      backgroundColor: alignRight ? theme.palette.grey[100] : theme.palette.background.paper,
                      color: theme.palette.text.primary,
                      borderRadius: theme.spacing(1.5),
                      borderTopLeftRadius: alignRight ? theme.spacing(1.5) : 0,
                      borderTopRightRadius: alignRight ? 0 : theme.spacing(1.5),
                      px: 2,
                      py: 0.75,
                      maxWidth: { xs: '100%', sm: '100%', md: '100%' },
                      boxShadow: alignRight ? theme.shadows[1] : 'none',
                      border: alignRight ? 'none' : `1px solid ${theme.palette.divider}`,
                    }}>
                      <Typography variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{message.text}</Typography>
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Stack>
        </Box>
      )}

      <MicrophonePermissionIndicator />
      <Typography variant="body2" color="text.secondary">
        {statusLabel}
      </Typography>
    </Stack>
  );
};

export default PersonaChatPanel;
